import { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Card, Col, Row, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import QrCodeIcon from "@mui/icons-material/QrCode";
import BagQRPrint from "./BagQRPrint/BagQRPrint";
import FilePreviewModal from "./FilePreviewModal/FilePreviewModal";
import CreateChildBagModal from "./ChildBag/CreateChildBagModal";
import { tableStyles } from "Constants/Style";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import PageContainer from "components/PageContainer/PageContainer";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";

const StockTransferInwardDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [showPreview, setShowPreview] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [showQRPrint, setShowQRPrint] = useState(false);
  const [qrPrintItem, setQrPrintItem] = useState<any>(null);
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchDetails = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await Axios.get(
        `${api.warehouse2.pendingStockTransfer.details}${id}`
      );
      setDetails(response?.data?.data);
    } catch (error) {
      console.error("Error fetching stock transfer inward details:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleOpen = useCallback((item: any) => {
    setSelectedItem(item);
    setShowModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowModal(false);
    setSelectedItem(null);
  }, []);

  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: LocalMallIcon,
        label: "Create Child Bag",
        permission: true,
        onClick: (item: any) => {
          handleOpen(item);
        },
        color: "primary",
      },
      {
        icon: QrCodeIcon,
        label: "Print QR",
        permission: true,
        onClick: (item: any) => {
          setQrPrintItem(item);
          setShowQRPrint(true);
        },
        color: "info",
      },
    ];
    return items;
  }, [handleOpen]);

  return (
    <PageContainer>
      <FormHeader
        title="Inward Details (Stock Transfer)"
        firstLabel="Stock Transfer"
        secondLabel="Inward Details"
        handleArrowClick={() => navigate("/warehouse-item-inward")}
      />

      {loading ? (
        <LoadingSpinner label="Loading details..." />
      ) : (
        <Card.Body className="m-0 p-0">

          <>
            <style>
              {`
          .sticky-column {
            width: 40px;
            position: sticky;
            right: 0;
            z-index: 1;
            background-color: #fff !important;
            border-left: 1px solid #dee2e6;
            text-align: center;
          }
          thead .sticky-column {
            background-color: #EEF0F7 !important;
          }
          .sticky-action {
            box-shadow: -4px 0 6px rgba(0, 0, 0, 0.1);
          }
          .table td,
          .table th {
            background-clip: padding-box;
          }
          .remark-cell {
            max-width: 300px;
            white-space: normal !important;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
        `}
            </style>
            <hr />
            <Row className="m-3">
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>GRN No. :</strong> {details?.grn_no || "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Dispatch No. :</strong>{" "}
                  {details?.dispatch?.dispatch_number || "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Inward Date :</strong>{" "}
                  {details?.inward_date
                    ? new Date(details?.inward_date).toISOString().split("T")[0]
                    : "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Transfer From :</strong>{" "}
                  {details?.dispatch?.transfer_from_warehouse?.warehouse_name ||
                    "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Transfer To :</strong>{" "}
                  {details?.warehouse?.warehouse_name || "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Received By :</strong>{" "}
                  {details?.received_by_details
                    ? `${details?.received_by_details?.first_name || ""} ${
                        details?.received_by_details?.last_name || ""
                      }`.trim() || details?.received_by_details?.username
                    : "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Remark :</strong> {details?.remarks || "-"}
                </p>
              </Col>
              <Col xs={12} md={6} lg={4}>
                <p className="mb-3">
                  <strong>Document :</strong>{" "}
                  {details?.document_file ? (
                    <button
                      onClick={() => setShowPreview(true)}
                      style={{
                        color: "blue",
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                        padding: 0,
                        textDecoration: "underline",
                      }}
                    >
                      View
                    </button>
                  ) : (
                    "-"
                  )}
                </p>
              </Col>
              {/* Document Viewer Popup */}
              <FilePreviewModal
                show={showPreview}
                onClose={() => setShowPreview(false)}
                url={details?.document_file}
              />
            </Row>

            <div className="mb-3 mt-3" style={{ overflowX: "auto" }}>
              <Table hover responsive className="align-middle text-center">
                <thead>
                  <tr>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "70px",
                      }}
                    >
                      Sr No.
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "120px",
                      }}
                    >
                      Item Name
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "120px",
                      }}
                    >
                      Item Code
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "120px",
                      }}
                    >
                      Category
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "120px",
                      }}
                    >
                      Sub Category
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "120px",
                      }}
                    >
                      Unit
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "150px",
                      }}
                    >
                      Received Quantity
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "120px",
                      }}
                    >
                      Bag No.
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        minWidth: "100px",
                      }}
                    >
                      Remark
                    </th>
                    <th
                      style={{
                        ...tableStyles.headerAlt,
                        ...tableStyles.actionHeader,
                        minWidth: "100px",
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {details?.inventory_items?.map((item: any, idx: number) => (
                    <tr key={item.inventory_item_id}>
                      <td style={tableStyles.cell}>{idx + 1}</td>
                      <td style={tableStyles.cell}>
                        {item?.master_item?.item_name || "-"}
                      </td>
                      <td style={tableStyles.cell}>
                        {item?.master_item?.item_code || "-"}
                      </td>
                      <td style={tableStyles.cell}>
                        {details?.dispatch?.dispatch_items?.find(
                          (di: any) => di.master_item_id === item.master_item_id
                        )?.category?.category_name ||
                          details?.dispatch?.dispatch_items?.find(
                            (di: any) =>
                              di.master_item_id === item.master_item_id
                          )?.category_name ||
                          "-"}
                      </td>
                      <td style={tableStyles.cell}>
                        {details?.dispatch?.dispatch_items?.find(
                          (di: any) => di.master_item_id === item.master_item_id
                        )?.sub_category?.sub_category_name ||
                          details?.dispatch?.dispatch_items?.find(
                            (di: any) =>
                              di.master_item_id === item.master_item_id
                          )?.sub_category_name ||
                          "-"}
                      </td>
                      <td style={tableStyles.cell}>
                        {details?.dispatch?.dispatch_items?.find(
                          (di: any) => di.master_item_id === item.master_item_id
                        )?.unit?.unit_name ||
                          details?.dispatch?.dispatch_items?.find(
                            (di: any) =>
                              di.master_item_id === item.master_item_id
                          )?.unit_name ||
                          "-"}
                      </td>
                      <td style={tableStyles.cell}>{item?.quantity || "-"}</td>
                      <td style={tableStyles.cell}>{item?.bag_no || "-"}</td>
                      <td className="remark-cell" style={tableStyles.cell}>
                        <TruncatedTextCell
                          text={item?.remarks || item?.remark}
                          maxWidth="300px"
                          maxLines={4}
                          rowIndex={idx}
                          fallbackText="-"
                        />
                      </td>
                      <td
                        className="sticky-column"
                        style={tableStyles.actionCell}
                      >
                        <div style={tableStyles.actionButtonContainer}>
                          <DynamicMenuItem row={item} menuItems={menuItems} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="d-flex justify-content-center gap-3 py-3">
              <Button
                variant="light"
                style={{
                  border: "1px solid #ced4da",
                  backgroundColor: "white",
                }}
                onClick={() => navigate(-1)}
              >
                <span
                  className="text-decoration-none"
                  style={{ color: "#495057" }}
                >
                  Back
                </span>
              </Button>
            </div>
          </>

          <CreateChildBagModal
            key={"createChildBag"}
            show={showModal}
            handleClose={handleClose}
            selectedItem={selectedItem}
            inventoryId={id}
            onSuccess={() => {
              // Refresh details after successful bag creation
              fetchDetails();
            }}
          />

          <BagQRPrint
            show={showQRPrint}
            handleClose={() => {
              setShowQRPrint(false);
              setQrPrintItem(null);
            }}
            inventoryItemId={qrPrintItem?.inventory_item_id || ""}
            bagNo={qrPrintItem?.bag_no || ""}
            autoPrint={true}
          />
        </Card.Body>
      )}
    </PageContainer>
  );
};

export default StockTransferInwardDetails;
